import React, { useCallback, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import { useParams } from 'react-router-dom';

import { usePromiseState } from '@futrli/shared-services';
import { PreviewAssumptionImpactsResponse, UnitAssumption } from '@futrli-sdk/consolidations';

import { AccountsImpactedContainer } from '../../../components/accounts-impacted/accounts-impacted-container';
import { getPredictionTypeArg } from '../../../context/utils';
import { ConsolidationPreviewImpactsRequestBody } from '../../../types';
import { readImpactPreview } from '../../../../../core/api/consolidated-assumptions';
import { useConsolidatedOrganisationPredictionBuilderContext } from '../../../context/consolidated-organisation-prediction-builder-context';
import { usePredictManagementContext } from '../../../../../predict-management/context/predict-management-context';

const DEBOUNCE_WAIT = 500;

export const ImpactPreview = () => {
  const { addErrorMessage } = usePredictManagementContext();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisatioId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisatioId || '';

  const {
    entity,
    forecast,
    handlePreviewImpactResponseChange,
    impactPreviewParams,
    isCashFlowDisabled,
    isQuickCopy,
    isSaveDisabled,
    paymentData,
    paymentType,
    predictionType,
    urlPredictionId
  } = useConsolidatedOrganisationPredictionBuilderContext();

  const [impactPreviewReq, setImpactPreviewReq, impactPreview] = usePromiseState<PreviewAssumptionImpactsResponse>();

  const forecastId = forecast?.id;

  const fetchImpactPreview = useMemo(() => {
    return debounce(async (requestData: ConsolidationPreviewImpactsRequestBody) => {
      if (forecastId) {
        let request;

        try {
          request = readImpactPreview(workspaceId, organisationId, forecastId, requestData);

          const newPreviewImpacts = await setImpactPreviewReq(request);
          handlePreviewImpactResponseChange(newPreviewImpacts);
        } catch (e) {
          if (impactPreviewReq.promise.current === request) {
            addErrorMessage?.(e as Error);
          }
        }
      }
    }, DEBOUNCE_WAIT);
  }, [
    addErrorMessage,
    forecastId,
    handlePreviewImpactResponseChange,
    impactPreviewReq.promise,
    organisationId,
    setImpactPreviewReq,
    workspaceId
  ]);

  const loadImpactPreview = useCallback(() => {
    if (impactPreviewParams && !isCashFlowDisabled && entity) {
      const price_variants = (impactPreviewParams as UnitAssumption).price_variants || [];
      const assumptionData = price_variants.length
        ? {
            ...impactPreviewParams,
            price_variants: price_variants.map(pv => ({
              ...pv,
              start_date:
                (pv.start_date as unknown) instanceof Date
                  ? ((pv.start_date as unknown) as Date).toISOString().split('T')[0]
                  : pv.start_date,
              end_date: pv.end_date
                ? (pv.end_date as unknown) instanceof Date
                  ? ((pv.end_date as unknown) as Date).toISOString().split('T')[0]
                  : pv.end_date
                : null
            }))
          }
        : impactPreviewParams;
      const requestData: ConsolidationPreviewImpactsRequestBody = {
        organisation_id: entity.id,
        assumption_id: urlPredictionId && !isQuickCopy ? urlPredictionId : undefined,
        assumption_type: getPredictionTypeArg(predictionType),
        assumption_data: assumptionData,
        payment_type: paymentType as ConsolidationPreviewImpactsRequestBody['payment_type'],
        payment_data: paymentData
      };

      fetchImpactPreview(requestData);
    }
  }, [
    entity,
    fetchImpactPreview,
    impactPreviewParams,
    isCashFlowDisabled,
    isQuickCopy,
    paymentData,
    paymentType,
    predictionType,
    urlPredictionId
  ]);

  // Load impact data (if params change)
  useEffect(() => {
    loadImpactPreview();
  }, [loadImpactPreview]);

  if (isSaveDisabled) {
    return null;
  }

  return <AccountsImpactedContainer predictionType={predictionType} previewData={impactPreview} />;
};
